import { useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import EditNoteIcon from '@mui/icons-material/EditNote';
import GroupsIcon from '@mui/icons-material/Groups';
import { ACTIVITY_TYPES, type ActivityType, type HouseholdDetail } from '@hv/shared';
import { useAddActivity } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { useNotify } from '../components/Notify';
import { Section } from './Section';

const META: Record<ActivityType, { label: string; color: string; icon: typeof CallIcon }> = {
  note: { label: 'Note', color: '#6b46c1', icon: EditNoteIcon },
  call: { label: 'Call', color: '#2f6fb0', icon: CallIcon },
  meeting: { label: 'Meeting', color: '#1f7a44', icon: GroupsIcon },
  email: { label: 'Email', color: '#93600c', icon: EmailIcon },
};

export function ActivitySection({ h }: { h: HouseholdDetail }) {
  const { can } = useAuth();
  const { notifyError } = useNotify();
  const add = useAddActivity();
  const [type, setType] = useState<ActivityType>('note');
  const [text, setText] = useState('');

  return (
    <Section title="Activity & Notes">
      {can('activities.write') && (
        <Stack gap={1} mb={2}>
          <Stack direction="row" gap={1}>
            <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value as ActivityType)} sx={{ width: 140 }}>
              {ACTIVITY_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {META[t].label}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Log an event or note about this family" value={text} onChange={(e) => setText(e.target.value)} multiline maxRows={6} sx={{ flex: 1 }} />
          </Stack>
          <Button
            variant="contained"
            sx={{ alignSelf: 'flex-start' }}
            disabled={!text.trim() || add.isPending}
            onClick={() => add.mutate({ id: h.id, type, text: text.trim() }, { onSuccess: () => setText(''), onError: (err) => notifyError(err, 'Logging activity failed') })}
          >
            Log activity
          </Button>
        </Stack>
      )}
      {h.activities.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No activity logged yet.
        </Typography>
      ) : (
        <Stack component="ol" gap={1.5} sx={{ listStyle: 'none', p: 0, m: 0 }} data-testid="timeline">
          {h.activities.map((a) => {
            const m = META[a.type];
            const Icon = m.icon;
            return (
              <Stack component="li" key={a.id} direction="row" gap={1.5}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: m.color, color: '#fff', display: 'grid', placeItems: 'center', flexShrink: 0 }} aria-hidden>
                  <Icon sx={{ fontSize: 16 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>{m.label}</strong> · {new Date(a.occurredAt).toLocaleString()} · {a.byUserName}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {a.text}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Section>
  );
}
